import React from "react";
import { Link } from "react-router-dom";
import { FileText, Calendar, Users, Inbox, MessageSquare, Smartphone, ArrowRight, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    icon: FileText,
    title: "Build (or pick) a regimen",
    role: "Admin · Doctor",
    body: "Choose from 40+ pre-loaded standard regimens — AC-T, FOLFOX, R-CHOP, CHOP, EC-T, gemcitabine + cisplatin, and more. Or compose a new one from the drug catalog (187 oncology drugs) with multi-drug administrations, day-of-cycle patterns, premedications, and lab dependencies.",
    detail: "OncoCadence prevents duplicate regimens at the database level — two providers in the same organization cannot create the same drug + dose + schedule combination twice. The dedup engine fuzzy-matches name and signature.",
  },
  {
    icon: Calendar,
    title: "Generate the patient calendar",
    role: "Doctor · Nurse",
    body: "Pick a patient, attach the regimen, set a start date. OncoCadence calculates every Drug Administration, Lab Draw, Clinic Visit, and Custom event across all cycles in one click. The calendar is immediately visible to the patient on their mobile PWA.",
    detail: "Drag-and-drop to reschedule any event. Pause-cycle and skip-cycle controls recalculate every downstream date automatically. A 'Medication Date Strip' summarises each day's drug exposure on a single horizontal card.",
  },
  {
    icon: Smartphone,
    title: "Patient lives on the mobile PWA",
    role: "Patient",
    body: "The patient receives an email invitation, installs the PWA to their home screen, and immediately sees their appointments grouped by date, medications with dosing instructions, side-effect logging tools, secure messaging, and notification settings — all offline-capable via Service Worker.",
    detail: "24 hours before every Drug Administration / Clinic Visit / Lab Draw, OncoCadence's APScheduler sweeps the database and sends an SMS reminder (via Twilio) to patients who toggled SMS on. Idempotent log prevents duplicate texts.",
  },
  {
    icon: Inbox,
    title: "Patient requests flow back to providers",
    role: "Patient → Doctor · Nurse",
    body: "Patients can request: reschedule an appointment, pause a cycle, refill a prescription, or report a side-effect concern. Each request creates an entry in the provider's unified Approvals queue with full context — current vs requested date, patient note, severity.",
    detail: "Providers approve or deny with an optional note. The decision fires three notifications in parallel: a Twilio SMS (if patient opted in), an SMTP email, and an in-app notification on the mobile PWA.",
  },
  {
    icon: MessageSquare,
    title: "Two-way messaging keeps everyone aligned",
    role: "All roles",
    body: "Free-text secure messaging between any patient and their assigned providers. Providers see a global Messages inbox with unread counts; patients see a single conversation per provider on their mobile shell.",
    detail: "Providers can also initiate a new conversation with any patient on their roster — useful for post-cycle wellness check-ins or proactive coaching.",
  },
  {
    icon: Users,
    title: "Provider directory + role management",
    role: "Admin · Organizer",
    body: "All four provider roles (Admin, Organizer, Doctor, Nurse) share a unified directory with search by name, email, specialty, department, address, phone. Admins/Organizers can add, edit, and reassign roles unlimited times via the Edit Provider modal.",
    detail: "Doctors and Nurses also see the Providers directory — read-only — so they can look up colleague contact info, specialty, and office address without admin intervention.",
  },
];

export default function HowItWorksPage() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-16 pb-12 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">How it works</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">From your first regimen to your patient&apos;s last dose — six steps.</h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">OncoCadence isn&apos;t another EHR. It&apos;s a focused workflow that sits between the EHR and the patient and removes the friction in between.</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald-500/60 via-emerald-500/20 to-transparent hidden sm:block" />
          <div className="space-y-6">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative pl-0 sm:pl-20 fade-up" style={{ animationDelay: `${i * 0.07}s` }} data-testid={`how-step-${i}`}>
                <div className="hidden sm:flex absolute left-0 top-2 w-14 h-14 rounded-full bg-[#0B1220] border-2 border-emerald-500/40 items-center justify-center">
                  <s.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="glass-card p-7">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-xl font-bold">{i + 1}. {s.title}</h3>
                    <span className="shrink-0 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[11px] font-bold text-emerald-400">{s.role}</span>
                  </div>
                  <p className="text-slate-300 leading-relaxed mb-3">{s.body}</p>
                  <p className="text-sm text-slate-500 leading-relaxed italic">→ {s.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 glass-card p-8 border-emerald-500/20">
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-7 h-7 text-emerald-400 shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-lg mb-1">Built for safety: nothing happens silently.</h3>
              <p className="text-sm text-slate-400">Every state-changing action — calendar generation, regimen edit, request decision, role change, password reset — is timestamped and traceable. Email + SMS + in-app notifications ensure no party is left wondering &quot;did this go through?&quot;</p>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors">
            See it live in 30 minutes <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
